import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import {
  Activity, Disc3, Wind, Mic, Music, AudioLines, Wrench, HeartHandshake,
  UtensilsCrossed, Flame, Trees, Sparkles, ArrowRight, Check, Images, ShoppingBag, Plus,
  Minus,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import QuantityStepper from "../components/QuantityStepper";
import AccommodationModal from "../components/AccommodationModal";
import CheckoutView from "../components/CheckoutView";
import BasketModal from "../components/BasketModal";
import SeoMeta from "../components/SeoMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLUG = "spring-feral-fest";

const gbp = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(Number(n) || 0);

// Display ticket copy exactly as entered in the admin console.
const firstReleaseText = (value) => value;

const EXPECT = [
  { icon: Activity, label: "Movement" },
  { icon: Disc3, label: "Ecstatic Dance" },
  { icon: Wind, label: "Breathwork" },
  { icon: Mic, label: "Powerful Talks" },
  { icon: Music, label: "Music" },
  { icon: AudioLines, label: "Sound" },
  { icon: Wrench, label: "Workshops" },
  { icon: HeartHandshake, label: "Connection" },
  { icon: UtensilsCrossed, label: "Food" },
  { icon: Flame, label: "Fire" },
  { icon: Trees, label: "Nature" },
  { icon: Sparkles, label: "Play" },
];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const pad = (n) => String(n).padStart(2, "0");

const Countdown = ({ deadline }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!deadline) return null;
  const diff = new Date(deadline).getTime() - now;
  if (diff <= 0) return null;
  const total = Math.max(0, Math.floor(diff / 1000));
  const hrs = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  return (
    <div data-testid="spring-countdown" className="bg-[#9b3d0a] text-[#f2e5de]">
      <div className="max-w-5xl mx-auto px-6 py-3.5 flex flex-col sm:flex-row items-center justify-center gap-x-5 gap-y-1 text-center">
        <span className="tracking-[0.18em] text-xs sm:text-sm font-bold uppercase">1st release ends in</span>
        <span className="feral-serif text-2xl sm:text-3xl tabular-nums tracking-wide" data-testid="spring-countdown-clock">
          {pad(hrs)}<span className="opacity-50">:</span>{pad(mins)}<span className="opacity-50">:</span>{pad(secs)}
        </span>
        <span className="text-[#f2e5de]/80 text-xs sm:text-sm">Book 1st release before it's gone.</span>
      </div>
    </div>
  );
};

/* ---- Ticket card ---- */
const TicketCard = ({ item, qty, onChange, featured }) => {
  const soldOut = item.soldOut;
  const capped = typeof item.remaining === "number";
  const atLimit = capped && qty >= item.remaining;
  return (
    <div
      data-testid={`spring-ticket-${item.id}`}
      className={`relative flex flex-col rounded-lg p-7 md:p-8 transition-all duration-300 ${
        featured
          ? "bg-[#2c6052] text-[#f3ece1] shadow-2xl md:-translate-y-3 ring-2 ring-[#ebc754]"
          : "bg-white text-[#1e231f] border border-[#d9c3a8] shadow-sm"
      } ${soldOut ? "opacity-60" : ""}`}
    >
      {featured && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ebc754] text-[#14110f] text-[11px] font-bold tracking-[0.16em] uppercase px-4 py-1">
          Best value
        </span>
      )}
      <p className={`tracking-[0.16em] text-[11px] font-bold uppercase mb-4 ${featured ? "text-[#ebc754]" : "text-[#9b3d0a]"}`}>
        {item.tagLine || "Limited"}
      </p>
      <h3 className="feral-serif text-3xl md:text-4xl leading-none mb-2">{item.label}</h3>
      <p className={`text-sm mb-6 ${featured ? "text-[#f3ece1]/70" : "text-[#6e5646]"}`}>{item.desc}</p>
      <p className="feral-serif text-5xl mb-1">{gbp(item.price)}</p>
      <p className={`text-xs ${item.leftLabel ? "mb-2" : "mb-7"} ${featured ? "text-[#f3ece1]/60" : "text-[#9a876f]"}`}>per person</p>
      {item.leftLabel && (
        <p className={`text-xs font-semibold mb-7 ${featured ? "text-[#ebc754]" : "text-[#9b3d0a]"}`}>{item.leftLabel}</p>
      )}

      <div className="mt-auto">
        {soldOut ? (
          <span className="block text-center bg-[#d9c3a8] text-[#7a6a58] text-xs font-bold tracking-[0.14em] uppercase px-4 py-3.5">
            Sold out
          </span>
        ) : qty > 0 ? (
          <div
            data-testid={`spring-qty-${item.id}`}
            className={` px-3 py-2.5 ${
              featured
                ? "bg-white/12 border border-white/10"
                : "bg-[#f3e7d6] border border-[#e0c9ad]"
            }`}
          >
            <div className="flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => onChange(Math.max(0, qty - 1))}
                aria-label={`Decrease ${item.label} quantity`}
                className="w-10 h-10 shrink-0 rounded-full border border-[#ebc754] text-[#9b3d0a] flex items-center justify-center hover:bg-[#ebc754] hover:text-[#14110f] transition-colors"
              >
                <Minus size={17} strokeWidth={2} />
              </button>

              <div className="min-w-[64px] text-center leading-none">
                <span
                  className={`block text-2xl font-bold tabular-nums ${
                    featured ? "text-[#fff8ed]" : "text-[#1e231f]"
                  }`}
                >
                  {qty}
                </span>
                <span
                  className={`block mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                    featured ? "text-[#fff8ed]/70" : "text-[#7a6a58]"
                  }`}
                >
                  {qty === 1 ? "ticket" : "tickets"}
                </span>
              </div>

              <button
                type="button"
                onClick={() => onChange(qty + 1)}
                disabled={atLimit}
                aria-label={`Increase ${item.label} quantity`}
                className="w-10 h-10 shrink-0 rounded-full border border-[#ebc754] text-[#9b3d0a] flex items-center justify-center hover:bg-[#ebc754] hover:text-[#14110f] disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#ebc754] transition-colors"
              >
                <Plus size={17} strokeWidth={2} />
              </button>
            </div>
          </div>
        ) : (
          <button
            data-testid={`spring-add-${item.id}`}
            onClick={() => onChange(1)}
            className={`w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-sm font-bold uppercase tracking-[0.12em] transition-colors ${
              featured
                ? "bg-[#ebc754] hover:bg-[#e0972f] text-[#14110f]"
                : "bg-[#9b3d0a] hover:bg-[#7e3208] text-[#f2e5de]"
            }`}
          >
            {item.cta} <Plus size={15} />
          </button>
        )}
      </div>
    </div>
  );
};

/* ---- Accommodation card ---- */
const StayCard = ({ item, qty, onChange, onDetails, premium, locked }) => {
  const soldOut = item.soldOut;
  const capped = typeof item.remaining === "number";
  return (
    <div
      data-testid={`spring-stay-${item.id}`}
      className={`flex flex-col rounded-lg overflow-hidden transition-all duration-300 ${
        premium ? "ring-2 ring-[#ebc754] shadow-2xl bg-white" : "border border-[#d9c3a8] bg-white shadow-sm"
      } ${soldOut ? "opacity-60" : ""}`}
    >
      <button type="button" onClick={onDetails} className="relative h-56 overflow-hidden group">
        <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {premium && (
          <span className="absolute top-4 left-4 bg-[#ebc754] text-[#14110f] text-[11px] font-bold tracking-[0.14em] uppercase px-3 py-1">
            Most private
          </span>
        )}
        <span className="absolute bottom-3 right-3 bg-black/55 text-[#f2e5de] text-[11px] font-semibold px-2.5 py-1 inline-flex items-center gap-1">
          <Images size={13} /> Photos
        </span>
      </button>
      <div className="p-6 md:p-7 flex flex-col flex-1">
        <p className="text-[#9b3d0a] text-[11px] tracking-[0.16em] uppercase font-bold mb-1">FERAL Weekend Stay</p>
        <h3 className="feral-serif text-[#9b3d0a] text-2xl leading-snug mb-1">{item.label}</h3>
        <p className="text-[#9b3d0a] text-xs font-semibold mb-4">{item.nights}</p>
        <p className="text-[#6e5646] text-sm leading-relaxed mb-5">{item.blurb}</p>
        <ul className="space-y-1.5 mb-6">
          {(item.features || []).slice(0, 5).map((f) => (
            <li key={f} className="flex items-start gap-2 text-[#3a3a30] text-sm">
              <Check size={15} className="text-[#5b8049] mt-0.5 shrink-0" /> {f}
            </li>
          ))}
        </ul>
        <div className="mt-auto pt-4 border-t border-[#e0d3bf] flex items-end justify-between">
          <div>
            <p className="feral-serif text-[#9b3d0a] text-3xl leading-none">{gbp(item.price)}</p>
            <p className="text-[#9a876f] text-xs mt-1">{item.perNight ? `\u00a3${item.perNight} per night \u00b7 3 nights` : "per room \u00b7 3 nights"}</p>
            {item.id === "spring-hayloft" && !soldOut && (
              <p className="text-[#9b3d0a] text-xs font-semibold mt-1" data-testid={`spring-left-${item.id}`}>
                1 left
              </p>
            )}
          </div>
          {soldOut ? (
            <span className="bg-[#d9c3a8] text-[#7a6a58] text-xs font-bold tracking-[0.14em] uppercase px-4 py-2.5" data-testid={`spring-soldout-${item.id}`}>
              Sold out
            </span>
          ) : locked ? (
            <span className="text-[#9b3d0a] text-xs font-semibold text-right max-w-[150px] leading-snug" data-testid={`spring-locked-${item.id}`}>
              Add a Weekend ticket to unlock accommodation
            </span>
          ) : qty > 0 ? (
            <QuantityStepper value={qty} onChange={onChange} testId={`spring-qty-${item.id}`} max={capped ? item.remaining : undefined} />
          ) : (
            <button
              data-testid={`spring-add-${item.id}`}
              onClick={() => onChange(1)}
              className="bg-[#9b3d0a] hover:bg-[#7e3208] text-[#f2e5de] px-6 py-2.5 text-sm font-bold uppercase tracking-[0.1em] inline-flex items-center gap-1.5 transition-colors"
            >
              Book <Plus size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const SpringFeralFest = () => {
  const [event, setEvent] = useState(null);
  const [cart, setCart] = useState(() => { try { return JSON.parse(localStorage.getItem("feral_cart_spring-feral-fest")) || {}; } catch (e) { return {}; } });
  useEffect(() => {
    try { localStorage.setItem("feral_cart_spring-feral-fest", JSON.stringify(cart)); } catch (e) { /* ignore */ }
    window.dispatchEvent(new Event("feral-cart-updated"));
  }, [cart]);
  const [detailItem, setDetailItem] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showBasket, setShowBasket] = useState(false);
  const [basketNotice, setBasketNotice] = useState("");
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    axios.get(`${API}/events/${SLUG}`).then((r) => setEvent(r.data)).catch(() => {});
  }, []);

  const dayTickets = event?.dayTickets || [];
  const weekendTickets = event?.weekendTickets || [];
  const accommodation = event?.accommodation || [];
  const images = event?.images || [];
  const heroPrice = event?.priceFrom || "From £65";
  const allItems = [...dayTickets, ...weekendTickets, ...accommodation];

  useEffect(() => {
    if (images.length <= 1) return undefined;
    const timer = setInterval(() => {
      setActiveImg((current) => (current + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [images.length]);

  const hasWeekend = weekendTickets.some((t) => (cart[t.id] || 0) > 0);
  const hasAccommodationInCart = accommodation.some((a) => (cart[a.id] || 0) > 0);

  useEffect(() => {
    if (!hasWeekend && hasAccommodationInCart) {
      setCart((c) => {
        const next = { ...c };
        accommodation.forEach((a) => delete next[a.id]);
        return next;
      });
      setBasketNotice(
        "Accommodation was also removed because a Weekend ticket is required."
      );
    }
  }, [hasWeekend, hasAccommodationInCart]); // eslint-disable-line

  const setQty = (id, qty) => {
    const item = allItems.find((candidate) => candidate.id === id);
    const safeQty =
      item && typeof item.remaining === "number"
        ? Math.min(qty, Math.max(0, item.remaining))
        : qty;
    if (safeQty > 0) setBasketNotice("");
    setCart((c) => {
      const next = { ...c };
      if (safeQty <= 0) delete next[id];
      else next[id] = safeQty;
      return next;
    });
  };

  const lines = useMemo(
    () =>
      Object.entries(cart)
        .map(([id, qty]) => {
          const it = allItems.find((i) => i.id === id);
          if (!it) return null;
          return {
            id,
            qty,
            name: firstReleaseText(it.name),
            price: it.price,
            unit: it.unit,
            max: typeof it.remaining === "number" ? it.remaining : undefined,
          };
        })
        .filter(Boolean),
    [cart, event] // eslint-disable-line
  );
  const subtotal = lines.reduce((s, l) => s + l.price * l.qty, 0);
  const count = lines.reduce((s, l) => s + l.qty, 0);

  if (showCheckout) {
    return (
      <CheckoutView
        event={SLUG}
        items={Object.entries(cart).map(([id, qty]) => ({ id, qty }))}
        eventName="Spring FERAL Fest"
        dateLine={event?.dateLine}
        lines={lines}
        total={subtotal}
        bookingConfig={{
          askHeard: true,
          bedChoiceIds: ["spring-ensuite", "spring-offsuite"],
          guestPlaceIds: [...dayTickets, ...weekendTickets].map((t) => t.id),
        }}
        onBack={() => setShowCheckout(false)}
      />
    );
  }

  const ticketType = (ticket) => {
    const source = `${ticket?.id || ""} ${ticket?.name || ""}`.toLowerCase();
    if (source.includes("wknd") || source.includes("weekend")) return "weekend";
    if (source.includes("sun") || source.includes("sunday")) return "sunday";
    return "saturday";
  };

  const ticketMeta = {
    saturday: { label: "Saturday", desc: "Full Saturday festival access.", cta: "Book Saturday", order: 0 },
    weekend: { label: "Full Weekend", desc: "Access to both Saturday + Sunday.", cta: "Book the weekend", featured: true, order: 1 },
    sunday: { label: "Sunday", desc: "Full Sunday festival access.", cta: "Book Sunday", order: 2 },
  };

  const releaseLabel = (name) => {
    let s = String(name || "");
    // Strip the day descriptor so whatever remains (e.g. "1st Release", "2nd Release", "Pre-Release") becomes the tag.
    s = s.replace(/full weekend|weekend|saturday|sunday|\bsat\b|\bsun\b/gi, "");
    s = s.replace(/[\u2014\u2013\u00b7|]/g, " "); // em/en dash, middot, pipe → space
    s = s.replace(/\s-\s/g, " ");                // spaced hyphen separator (keeps intra-word hyphens like Pre-Release)
    s = s.replace(/^[\s\-\u2013\u2014\u00b7|]+|[\s\-\u2013\u2014\u00b7|]+$/g, "");
    return s.replace(/\s+/g, " ").trim();
  };

  const releaseRank = (name) => {
    const s = String(name || "").toLowerCase();
    if (/\b1st\b|first|pre[-\s]?release/.test(s)) return 0;
    if (/\b2nd\b|second/.test(s)) return 1;
    if (/\b3rd\b|third/.test(s)) return 2;
    return 9;
  };

  const allTickets = [...dayTickets, ...weekendTickets];
  const ticketView = allTickets
    .map((t) => {
      const type = ticketType(t);
      const meta = ticketMeta[type] || {};
      const release = releaseLabel(t.name);
      return {
        ...t,
        label: meta.label || t.name,
        desc: meta.desc || t.desc || "",
        cta: meta.cta || "Book",
        tagLine: release ? `${release} \u00b7 Limited` : "Limited",
        featured: Boolean(meta.featured || t.featured) && !t.soldOut,
        sortOrder: meta.order ?? 99,
      };
    })
    .sort((a, b) => {
      const r = releaseRank(a.name) - releaseRank(b.name);
      if (r !== 0) return r;
      return (a.sortOrder ?? 99) - (b.sortOrder ?? 99);
    });

  const stayLabels = {
    "spring-ensuite": "En-suite Double / Twin",
    "spring-offsuite": "Off-suite Double / Twin",
    "spring-hayloft": "The Hayloft",
    "spring-motorhome": "Motorhome Pitch",
  };
  const stayView = accommodation.map((a) => ({
    ...a,
    label: stayLabels[a.id] || a.name,
    premium: a.id === "spring-hayloft",
  }));

  return (
    <div className="bg-[#f2e5de] min-h-screen">
      <SeoMeta
        title="Spring FERAL Fest 2027 | Spring Equinox Festival in Anglesey"
        description="Join FERAL Fest Spring Awakening on 13–14 March 2027 at Plas Llechylched, Anglesey for movement, breathwork, sound, workshops, talks, food, fire and community."
        canonicalPath="/events/spring-feral-fest"
        image="/spring-fest-lawn.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Event",
          name: "Spring FERAL Fest 2027",
          description: "A Spring Equinox FERAL festival with movement, breathwork, sound, workshops, talks, food, fire and community.",
          startDate: "2027-03-13T10:00:00+00:00",
          endDate: "2027-03-14T20:00:00+00:00",
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          eventStatus: "https://schema.org/EventScheduled",
          location: {
            "@type": "Place",
            name: "Plas Llechylched",
            address: "Anglesey, Wales, United Kingdom",
          },
          image: ["https://feralcollective.co.uk/spring-fest-lawn.jpg"],
          organizer: {
            "@type": "Organization",
            name: "FERAL Collective",
            url: "https://feralcollective.co.uk/",
          },
          offers: [
            { "@type": "Offer", price: "65", priceCurrency: "GBP", url: "https://feralcollective.co.uk/events/spring-feral-fest", availability: "https://schema.org/InStock" },
            { "@type": "Offer", price: "95", priceCurrency: "GBP", url: "https://feralcollective.co.uk/events/spring-feral-fest", availability: "https://schema.org/InStock" },
          ],
        }}
      />
      <Navbar tone="sunset" />

      {/* HERO — same visual structure as The Rebel's Retreat */}
      <section className="relative min-h-[94vh] flex items-end overflow-hidden">
        {(images.length ? images : [event?.cardImage]).filter(Boolean).map((src, i) => (
          <img
            key={`${src}-${i}`}
            src={src}
            alt="Spring FERAL Fest"
            data-testid={i === activeImg ? "spring-hero-image" : undefined}
            className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ${
              i === activeImg ? "opacity-100" : "opacity-0"
            }`}
            loading={i === 0 ? "eager" : "lazy"}
            style={{ filter: "contrast(1.05) saturate(1.03)" }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-[#2c6052] via-[#2c6052]/48 to-[#2c6052]/18" />

        <div className="relative z-10 max-w-5xl mx-auto w-full px-6 pb-16 md:pb-24">
          <p className="text-[#f0dbbe] tracking-[0.3em] text-xs sm:text-sm font-semibold uppercase mb-4">
            Spring Equinox Empowerment Festival
          </p>
          <h1 className="feral-serif text-[#ebc754] text-5xl sm:text-7xl lg:text-8xl leading-[0.92] mb-5">
            FERAL Fest Spring Awakening
          </h1>
          <p className="text-[#f2e5de]/90 tracking-[0.12em] text-sm sm:text-base font-semibold uppercase mb-3">
            {event?.dateLine} · {event?.venue}
          </p>
          <p className="text-[#f2e5de]/75 text-sm sm:text-base mb-9">
            {event?.time} · {heroPrice}
          </p>
          <button
            onClick={() => scrollTo("tickets")}
            data-testid="spring-hero-cta"
            className="inline-flex items-center gap-3 bg-[#ebc754] hover:bg-[#e0972f] text-[#14110f] px-9 py-4 tracking-[0.14em] text-sm font-bold uppercase transition-colors"
          >
            Book 1st release tickets <ArrowRight size={17} />
          </button>
        </div>

        {images.length > 1 && (
          <div className="absolute bottom-6 right-6 z-10 flex gap-2">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActiveImg(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-2 rounded-full transition-all ${
                  i === activeImg ? "w-6 bg-[#ebc754]" : "w-2 bg-white/50 hover:bg-white/80"
                }`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Spring booking notes */}
      <section className="feral-texture px-6 py-10 border-b border-[#d9c3a8]">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#f3e7d6] border border-[#d9c3a8] rounded-md p-4 md:p-5 space-y-2 text-center">
            <p className="text-[#9b3d0a] font-semibold text-sm">{firstReleaseText(event?.limitedNote)}</p>
            {event?.accommodationNote && <p className="text-[#6e5646] text-sm italic">{event.accommodationNote}</p>}
            <p className="text-[#6e5646] text-sm italic">{event?.ageNote}</p>
          </div>
        </div>
      </section>

      {/* Release countdown */}
      <Countdown deadline={event?.preReleaseEndsAt} />

      {/* Description + highlights */}
      <section className="feral-texture px-6 py-16">
        <div className="max-w-3xl mx-auto space-y-5 text-[#1e231f] text-base sm:text-lg leading-relaxed">
          {(event?.description || []).map((p, i) => (
            <p key={i}>{firstReleaseText(p)}</p>
          ))}
        </div>

        <div className="max-w-5xl mx-auto mt-14">
          <p className="text-[#9b3d0a] tracking-[0.2em] text-xs font-bold uppercase mb-6 text-center">Festival highlights</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
            {EXPECT.map(({ icon: Icon, label }) => (
              <div key={label} className="bg-white border border-[#d9c3a8] rounded-lg py-7 px-4 flex flex-col items-center gap-3 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <Icon size={28} className="text-[#9b3d0a]" strokeWidth={1.6} />
                <span className="text-[#1e231f] text-sm font-semibold tracking-wide">{label}</span>
              </div>
            ))}
          </div>
          <p className="text-[#9b3d0a] tracking-[0.22em] text-sm font-bold uppercase mt-10 text-center">Full line-up coming soon</p>
        </div>
      </section>

      {/* GET YOUR TICKET */}
      <section id="tickets" className="feral-texture bg-[#e2c7b0] py-16 md:py-20 px-6 scroll-mt-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#9b3d0a] tracking-[0.22em] text-sm font-semibold uppercase mb-3">Get your ticket</p>
            <h2 className="feral-serif text-[#9b3d0a] text-4xl md:text-5xl mb-3">Book your place</h2>
            <p className="text-[#6e5646] text-sm max-w-xl mx-auto">1st release · limited availability. Once the allocated 1st release tickets sell out, prices will increase.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7 items-start max-w-5xl mx-auto">
            {ticketView.map((t) => (
              <TicketCard key={t.id} item={t} featured={t.featured} qty={cart[t.id] || 0} onChange={(q) => setQty(t.id, q)} />
            ))}
          </div>
        </div>
      </section>

      {/* STAY AT FERAL */}
      <section id="stay" className="feral-texture py-16 md:py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mb-12">
            <p className="text-[#9b3d0a] tracking-[0.2em] text-xs font-bold uppercase mb-4">Stay at FERAL</p>
            <h2 className="feral-serif text-[#9b3d0a] text-4xl md:text-5xl leading-[1.05] mb-5">
              Don't just come to FERAL. Live it for the weekend.
            </h2>
            <p className="text-[#3a3a30] text-base md:text-lg leading-relaxed mb-3">
              All stays are for three nights, Friday to Monday, at Plas L. Guests staying on site get access to an exclusive Friday evening Supper Club with the hosts before the main festival begins.
            </p>
            <div className="inline-flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#6e5646] italic">
              <span>Breakfast not included.</span>
              <span>Festival tickets are separate from accommodation.</span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
            {stayView.map((a) => (
              <StayCard
                key={a.id}
                item={a}
                premium={a.premium}
                qty={cart[a.id] || 0}
                onChange={(q) => setQty(a.id, q)}
                onDetails={() => setDetailItem(a)}
                locked={!hasWeekend}
              />
            ))}
          </div>
        </div>
      </section>

      {/* SPRING EQUINOX close */}
      <section className="relative py-24 md:py-32 px-6 overflow-hidden">
        <img src="/plas-l-exterior.jpeg" alt="The tree-lined driveway to Plas L" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#2c6052]/60" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="feral-serif text-[#ebc754] text-5xl md:text-7xl leading-none mb-8">Winter is over.</h2>
          <p className="text-[#f2e5de]/85 text-xl md:text-2xl mb-8">Time to emerge.</p>
          <p className="text-[#f2e5de]/75 text-base md:text-lg leading-relaxed max-w-xl mx-auto mb-10">
            Spring FERAL Fest lands at the Spring Equinox — a natural point of change, momentum and new beginnings. Come exactly as you are. Leave with something moving.
          </p>
          <div className="feral-serif text-[#ebc754] text-2xl md:text-3xl space-y-1">
            <p>Connection is medicine.</p>
            <p className="text-[#f2e5de]">Be Free, Be FERAL.</p>
          </div>
          <button
            onClick={() => scrollTo("tickets")}
            className="mt-12 inline-flex items-center gap-3 bg-[#ebc754] hover:bg-[#e0972f] text-[#14110f] px-9 py-4 tracking-[0.14em] text-sm font-bold uppercase transition-colors"
          >
            Book 1st release tickets <ArrowRight size={17} />
          </button>
        </div>
      </section>

      <Footer />

      {/* Accommodation detail modal */}
      <AccommodationModal
        item={detailItem}
        open={detailItem !== null}
        onOpenChange={(o) => !o && setDetailItem(null)}
        qty={detailItem ? cart[detailItem.id] || 0 : 0}
        onChange={(q) => detailItem && setQty(detailItem.id, q)}
        disabled={!hasWeekend}
        onViewBasket={() => {
          setDetailItem(null);
          setShowBasket(true);
        }}
      />

      <BasketModal
        open={showBasket}
        onClose={() => setShowBasket(false)}
        lines={lines}
        subtotal={subtotal}
        notice={basketNotice}
        onRemove={(id) => setQty(id, 0)}
        onQtyChange={(id, qty) => setQty(id, qty)}
        onCheckout={() => {
          setShowBasket(false);
          setShowCheckout(true);
        }}
      />

      {/* Compact floating basket */}
      {count > 0 && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-auto z-50 pointer-events-none">
          <button
            onClick={() => setShowBasket(true)}
            data-testid="spring-sticky-checkout"
            className="pointer-events-auto w-full sm:w-auto bg-[#9b3d0a] hover:bg-[#7e3208] text-[#f2e5de] shadow-2xl px-5 py-3.5 flex items-center justify-between sm:justify-start gap-5 transition-all hover:-translate-y-0.5"
          >
            <span className="inline-flex items-center gap-2.5 text-sm font-semibold">
              <span className="relative inline-flex">
                <ShoppingBag size={19} />
                <span className="absolute -top-2 -right-2 min-w-4 h-4 px-1 bg-[#ebc754] text-[#14110f] text-[10px] font-bold flex items-center justify-center">
                  {count}
                </span>
              </span>
              Basket
            </span>
            <span className="font-semibold text-sm">{gbp(subtotal)}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default SpringFeralFest;
